import { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "@foris/avocado-suite";
import { resetInstructorRemovalFlow } from "@context/instructorRemovalFlow/useInstructorRemovalFlowStore";
import type { InstructorRemovalOutcome } from "@context/instructorRemovalFlow/types";
import { setActiveProposal } from "@context/useAssignmentProposalStore";
import type { SessionCrudResult } from "@models/ISchema";
import type { IParams } from "@models/IParams";
import {
  useApplyAssignmentProposal,
  usePreApproveAssignmentProposal,
  useSubmitAssignmentProposal,
} from "@services/collections/assignment-proposal/assignmentProposalServices";
import type {
  ProposalIntent,
  ReviewerOption,
} from "@services/types/assignment-proposal/assignmentProposalTypes";
import {
  buildDecisionInput,
  buildProposalInput,
  outcomeErrorKey,
  proposalDecisionKind,
  skipsIntermediateReview,
} from "@utils/assignmentProposal";
import type { ConfirmProposalModalProps } from "../components/confirm-proposal-modal/ConfirmProposalModal";
import { AppContext } from "../context/EditSessionsContext";
import { applyValidationResponse } from "../utils/applyValidationResponse";
import { buildAssignationChangesets, changesetsFingerprint } from "../utils/assignationChangesets";
import { hasValidationIssues } from "../utils/hasValidationIssues";
import { useActiveProposal } from "./useActiveProposal";
import { useLeaveDecidedProposal } from "./useLeaveDecidedProposal";
import { useReturnToCurrentSchedule } from "./useReturnToCurrentSchedule";
import { useSyncProposalUrl } from "./useSyncProposalUrl";

/** The instructor-removal checks of the editor, which run around an application. */
interface InstructorRemovalChecks {
  isEnabled: boolean;
  prepareBeforeSave: () => Promise<boolean>;
  verifySavedLeague: () => Promise<Pick<InstructorRemovalOutcome, "reasons"> | null>;
}

type ConfirmParams = { reviewer: ReviewerOption | null; comment: string };

type FailedOutcome =
  | { kind: "validation"; validation: SessionCrudResult }
  | { kind: "rejected"; errorCode: string }
  | { kind: "failed" };

/**
 * What confirming the validation of a proposal does: the modal of its decision (send or resend,
 * intermediate approval, application), the requests and the confirmation after a send. Only an
 * application changes Darwin, so it runs the instructor-removal checks of a direct save around it.
 * The page's global loading is not used: the page renders nothing while it is on, which would
 * unmount the modal mid-request.
 */
export const useProposalSubmission = (instructorRemoval: InstructorRemovalChecks) => {
  const { state, dispatch } = useContext(AppContext);
  const { scenario: scenarioId, origin: originId }: IParams = useParams();
  const { t } = useTranslation();
  const activeProposal = useActiveProposal();
  const syncProposalUrl = useSyncProposalUrl();
  const returnToCurrentSchedule = useReturnToCurrentSchedule();
  const leaveDecidedProposal = useLeaveDecidedProposal();
  const submission = useSubmitAssignmentProposal();
  const preApproval = usePreApproveAssignmentProposal();
  const application = useApplyAssignmentProposal();
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sentConfirmation, setSentConfirmation] = useState<"sent" | "resent" | null>(null);
  // The application spans its checks and its request, not only the mutation.
  const [isApplicationRunning, setIsApplicationRunning] = useState(false);
  const isApplicationInFlight = useRef(false);
  const isCancelled = useRef(false);
  // A request can finish after the user left the editor: it must not touch a later visit.
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  const kind = proposalDecisionKind(activeProposal);
  const linkId = state?.link?.info?.id;

  /** A review goes on only with the intermediate approval or the application. */
  const canOpen = activeProposal?.mode !== "review" || kind === "apply" || kind === "pre-approve";

  const open = () => {
    if (!canOpen) return;

    setErrorMessage(null);
    setIsOpen(true);
  };

  const cancel = () => {
    isCancelled.current = true;
    setIsOpen(false);
    setErrorMessage(null);
  };

  // The guards of applyValidationResponse skip a result while one is stored: it replaces it.
  const showNewValidation = (validation: SessionCrudResult) => {
    setIsOpen(false);
    applyValidationResponse({
      responseData: validation,
      state: {
        ...state,
        result: { ...state?.result, resultValidation: null, createValidation: null },
      },
      dispatch,
    });
  };

  const settleFailure = (outcome: FailedOutcome, intent: ProposalIntent) => {
    if (outcome.kind === "validation") showNewValidation(outcome.validation);
    else setErrorMessage(t(outcomeErrorKey(outcome, intent)));
  };

  const send = async ({ reviewer, comment }: ConfirmParams) => {
    if (!reviewer || !linkId) return;

    setErrorMessage(null);
    const outcome = await submission.submit({
      scenarioId,
      originId,
      input: buildProposalInput({
        linkId: String(linkId),
        changesets: buildAssignationChangesets(state?.form),
        draft: false,
        proposalId: activeProposal?.proposalId,
        reviewerUsernames: [reviewer.value],
        comment,
      }),
    });

    if (!outcome || !isMounted.current) return;

    if (outcome.kind === "sent") {
      // Locked before the confirmation shows, so nothing on screen can change what was sent.
      setActiveProposal({
        linkId: String(linkId),
        proposalId: outcome.proposalId,
        mode: "sent",
        origin: activeProposal?.origin ?? "editor",
        rejectionReason: null,
        reviewer,
      });
      syncProposalUrl(outcome.proposalId);
      setIsOpen(false);
      setSentConfirmation(kind === "resend" ? "resent" : "sent");
      return;
    }

    if (outcome.kind === "rejected" && outcome.savedProposalId) {
      // The backend saved the content although it did not send it: a retry sends that proposal.
      const isSavedCorrection = outcome.savedState === "rejected";

      setActiveProposal({
        linkId: String(linkId),
        proposalId: outcome.savedProposalId,
        mode: isSavedCorrection ? "rejected" : "draft",
        origin: activeProposal?.origin ?? "editor",
        rejectionReason: activeProposal?.rejectionReason ?? null,
        reviewer: activeProposal?.reviewer ?? null,
      });
      syncProposalUrl(outcome.savedProposalId);
      setErrorMessage(
        t(
          isSavedCorrection
            ? outcomeErrorKey(outcome, "send")
            : "assignation-edit.proposal.errors.SAVED_DRAFT_NOT_SENT",
        ),
      );
      return;
    }

    settleFailure(outcome, "send");
  };

  const preApprove = async ({ comment }: ConfirmParams) => {
    if (!activeProposal) return;

    setErrorMessage(null);
    const changesets = buildAssignationChangesets(state?.form);
    const fingerprint = changesetsFingerprint(changesets);
    // The backend takes any changeset with sessions as an adjustment: they travel only on a change.
    const isAdjusted = fingerprint !== activeProposal.loadedChangesets;

    const outcome = await preApproval.submit({
      scenarioId,
      originId,
      input: buildDecisionInput({
        linkId: activeProposal.linkId,
        proposalId: activeProposal.proposalId,
        changesets: isAdjusted ? changesets : null,
        comment,
      }),
    });

    if (!outcome || !isMounted.current) return;

    if (outcome.kind === "recorded") {
      setIsOpen(false);
      toast.success(t("assignation-edit.proposal.toast.pre-approved"), { autoClose: 3000 });
      leaveDecidedProposal();
      return;
    }

    if (outcome.kind === "rejected" && outcome.savedProposalId && isAdjusted) {
      // The adjustment is the proposal's content now: a retry only has to record the approval.
      setActiveProposal({ ...activeProposal, loadedChangesets: fingerprint });
      setErrorMessage(t("assignation-edit.proposal.errors.ADJUSTMENT_SAVED_NOT_APPROVED"));
      return;
    }

    settleFailure(outcome, "pre-approve");
  };

  const runApplication = async (comment: string) => {
    if (!activeProposal) return;

    if (instructorRemoval.isEnabled && !(await instructorRemoval.prepareBeforeSave())) {
      // The instructor-removal modal explains it, and its retry opens this modal again.
      if (isMounted.current) setIsOpen(false);
      return;
    }

    if (isCancelled.current || !isMounted.current) {
      if (instructorRemoval.isEnabled) resetInstructorRemovalFlow();
      return;
    }

    const outcome = await application.submit({
      scenarioId,
      originId,
      input: buildDecisionInput({
        linkId: activeProposal.linkId,
        proposalId: activeProposal.proposalId,
        // The changesets always travel: they are what is applied, adjustments included.
        changesets: buildAssignationChangesets(state?.form),
        comment,
      }),
    });

    if (!outcome || !isMounted.current) return;

    if (outcome.kind === "recorded") {
      setIsOpen(false);
      toast.success(t("assignation-edit.proposal.toast.applied"), { autoClose: 3000 });

      const removal = instructorRemoval.isEnabled
        ? await instructorRemoval.verifySavedLeague()
        : null;
      if (!isMounted.current) return;

      // With a removal to report the user stays: its modal reloads the link when it is closed.
      const isRemovalPending = instructorRemoval.isEnabled && !(removal && !removal.reasons.length);
      if (isRemovalPending) returnToCurrentSchedule({ reload: false });
      else leaveDecidedProposal();
      return;
    }

    // Darwin did not change: there is nothing for the instructor-removal checks to verify.
    if (instructorRemoval.isEnabled) resetInstructorRemovalFlow();

    if (outcome.kind === "rejected" && outcome.savedProposalId) {
      setErrorMessage(t("assignation-edit.proposal.errors.ADJUSTMENT_SAVED_NOT_APPLIED"));
      return;
    }

    settleFailure(outcome, "apply");
  };

  const apply = async ({ comment }: ConfirmParams) => {
    if (isApplicationInFlight.current) return;

    isApplicationInFlight.current = true;
    isCancelled.current = false;
    setIsApplicationRunning(true);
    setErrorMessage(null);

    try {
      await runApplication(comment);
    } finally {
      isApplicationInFlight.current = false;
      if (isMounted.current) setIsApplicationRunning(false);
    }
  };

  const modalProps: ConfirmProposalModalProps = (() => {
    const common = { errorMessage, onCancel: cancel };

    if (kind === "apply") {
      return {
        ...common,
        kind,
        skipsIntermediateReview: skipsIntermediateReview(activeProposal),
        isSubmitting: isApplicationRunning,
        onConfirm: apply,
      };
    }

    if (kind === "pre-approve") {
      return { ...common, kind, isSubmitting: preApproval.isSubmitting, onConfirm: preApprove };
    }

    return {
      ...common,
      kind: "send",
      hasIssues: hasValidationIssues(state?.result),
      initialReviewer: kind === "resend" ? activeProposal?.reviewer : null,
      isSubmitting: submission.isSubmitting,
      onConfirm: send,
    };
  })();

  return { isOpen, open, modalProps, sentConfirmation };
};
